import type {
  BillingPaymentMethodConfigResponse,
  BusinessInfoPayload,
  BusinessInfoPrefill,
  CampaignInfoPayload,
  ClinicSetupState,
  ImportReviewData,
  MerchantBusinessDetails,
  MerchantOnboardingLocationsResponse,
  MerchantOnboardingResponse,
  MerchantOnboardingStatusResponse,
  ProvisionPhoneResponse,
  RegistrationStatus,
  SetupReadiness,
  StripeAccountSessionResponse,
  StripeAchCapabilityResponse,
  SubmitBillingCardResponse,
} from "@/types/clinic-setup";
import type { ClinicSetupFlowType } from "@/types/enums/clinic-setup";
import type { AffirmAccessResponse } from "@/types/merchant-account";

import { requestData } from "@/api/api-config";

// Re-export all types so existing imports from "@/api/clinic-setup" continue to work
export type {
  BillingPaymentMethodConfigResponse,
  BusinessInfoPayload,
  BusinessInfoPrefill,
  CampaignInfoPayload,
  ClinicSetupMetadata,
  ClinicSetupState,
  ImportReviewData,
  ImportReviewProductItem,
  ImportReviewServiceItem,
  LocationMerchantStatus,
  MerchantBusinessDetails,
  MerchantCandidateStatus,
  MerchantOnboardingFallback,
  MerchantOnboardingLocationsResponse,
  MerchantOnboardingResponse,
  MerchantOnboardingStatusResponse,
  PosTerminalCatalogResponse,
  PosTerminalOption,
  PosOrderFulfillment,
  PosOrderPayment,
  PosTerminalOrderResponse,
  ProvisionPhoneResponse,
  RegistrationStatus,
  SetupCapability,
  SetupCapabilityState,
  SetupProgress,
  SetupReadiness,
  StripeAccountSessionResponse,
  SubmitBillingCardResponse,
} from "@/types/clinic-setup";
export { BusinessStructure } from "@/types/clinic-setup";
export type {
  ClinicSetupFlowType,
  ClinicSetupStep,
  StepStatus,
} from "@/types/enums/clinic-setup";

export function fetchClinicSetupState() {
  return requestData<ClinicSetupState>("admin/clinic-setup/state");
}

/** Capability readiness for the current tenant. */
/** `tenant` reads another clinic's readiness, e.g. ahead of switching to it. */
export function fetchClinicSetupReadiness(tenant?: string) {
  return requestData<SetupReadiness>(
    "admin/clinic-setup/readiness",
    "get",
    tenant ? { headers: { TENANT: tenant } } : undefined,
  );
}

/** catchError: false so a refusal rejects instead of resolving to null. */
export function completeClinicSetupStep(step: string) {
  return requestData<boolean>(
    `admin/clinic-setup/step/${step}/complete`,
    "put",
    undefined,
    false,
  );
}

/** Advance past a step, on the record as skipped rather than done. */
export function skipClinicSetupStep(step: string) {
  return requestData<boolean>(
    `admin/clinic-setup/step/${step}/complete?skipped=true`,
    "put",
    undefined,
    false,
  );
}

export function revertClinicSetupStep(step: string) {
  return requestData<boolean>(`admin/clinic-setup/step/${step}/revert`, "put");
}

export function setClinicSetupFlowType(flowType: ClinicSetupFlowType) {
  return requestData<boolean>("admin/clinic-setup/flow-type", "put", {
    json: { flow_type: flowType },
  });
}

export function updateClinicSetupMetadata(metadata: Record<string, unknown>) {
  return requestData<boolean>("admin/clinic-setup/metadata", "put", {
    json: { metadata },
  });
}

/** Record that the clinic won't be sending from its own domain. */
export function skipEmailDomainSetup() {
  return requestData<boolean>(
    "admin/clinic-setup/email/skip",
    "put",
    undefined,
    false,
  );
}

/** Close the "You're all set" panel for this clinic, permanently. */
export function dismissSetupCompletion() {
  return requestData<boolean>(
    "admin/clinic-setup/completion/dismiss",
    "put",
    undefined,
    false,
  );
}

/** Record that an admin clicked through the completion screen. */
export function finishClinicSetup() {
  return requestData<boolean>(
    "admin/clinic-setup/completion/finish",
    "put",
    undefined,
    false,
  );
}

export function skipClinicSetup() {
  return requestData<boolean>("admin/clinic-setup/skip", "put");
}

export function provisionClinicPhone(number: string) {
  // catchError: false so 4xx responses (e.g. 409 when another clinic
  // already owns this number, or 409 when this clinic has a different
  // primary on file) throw and the caller's try/catch can short-circuit
  // before submitting SMS registration. With the default catchError=true,
  // the failure would silently resolve to null and handleProvision would
  // continue into handleSubmitRegistration as if the buy succeeded.
  return requestData<ProvisionPhoneResponse>(
    "admin/clinic-setup/phone-setup/provision",
    "post",
    { json: { number } },
    false,
  );
}

/**
 * catchError: false so a failure rejects instead of resolving to null.
 *
 * `businessDetails` prefills every processor the clinic is being underwritten
 * on, and only counts the first time — once a processor's form is open, it
 * owns those fields.
 */
export function startMerchantOnboarding(
  locationId?: string,
  businessDetails?: MerchantBusinessDetails,
) {
  const json: Record<string, unknown> = {};
  if (locationId) json.location_id = locationId;
  if (businessDetails) {
    json.business_details = {
      legal_name: businessDetails.legalName || null,
      dba_name: businessDetails.dbaName || null,
      business_structure: businessDetails.businessStructure || null,
      email: businessDetails.email || null,
      phone: businessDetails.phone || null,
      website: businessDetails.website || null,
    };
  }
  return requestData<MerchantOnboardingResponse>(
    "admin/clinic-setup/merchant-onboarding/start",
    "post",
    Object.keys(json).length > 0 ? { json } : undefined,
    false,
  );
}

/**
 * A fresh session for Stripe's embedded onboarding form. Connect.js calls this
 * again whenever a session expires, so it must mint a new one every time.
 */
export function createStripeAccountSession(locationId?: string | null) {
  return requestData<StripeAccountSessionResponse>(
    "admin/clinic-setup/merchant-onboarding/account-session",
    "post",
    locationId ? { json: { location_id: locationId } } : undefined,
    false,
  );
}

/** A fresh session for Stripe's notification banner on one connected account. */
export function createStripeNotificationBannerSession(stripeAccountId: string) {
  return requestData<StripeAccountSessionResponse>(
    "admin/clinic-setup/merchant-onboarding/notification-banner-session",
    "post",
    { json: { stripe_account_id: stripeAccountId } },
    false,
  );
}

export function getStripeAchCapability(merchantAccountId: string) {
  return requestData<StripeAchCapabilityResponse>(
    "admin/clinic-setup/merchant-onboarding/ach-capability",
    "get",
    { searchParams: { merchant_account_id: merchantAccountId } },
  );
}

/** Ask Stripe to turn on ACH for an account created before we requested it. */
export function requestStripeAchCapability(merchantAccountId: string) {
  return requestData<StripeAchCapabilityResponse>(
    "admin/clinic-setup/merchant-onboarding/ach-capability",
    "post",
    { json: { merchant_account_id: merchantAccountId } },
    false,
  );
}

/**
 * Ask Decoda to turn on Affirm on card readers. Affirm needs its own agreement,
 * so this records the ask; Decoda turns it on once the agreement is signed.
 */
export function requestAffirmAccess(merchantAccountId: string) {
  return requestData<AffirmAccessResponse>(
    "admin/clinic-setup/merchant-onboarding/affirm-request",
    "post",
    { json: { merchant_account_id: merchantAccountId } },
    false,
  );
}

export function getMerchantOnboardingLocations() {
  return requestData<MerchantOnboardingLocationsResponse>(
    "admin/clinic-setup/merchant-onboarding/locations",
  );
}

export function getMerchantOnboardingStatus(locationId?: string) {
  return requestData<MerchantOnboardingStatusResponse>(
    "admin/clinic-setup/merchant-onboarding/status",
    "get",
    locationId ? { searchParams: { location_id: locationId } } : undefined,
  );
}

/**
 * `merchantId` names which of the location's applications the status is
 * about — the Rainforest form must not write onto the Stripe one.
 */
export function updateMerchantOnboardingStatus(
  status: string,
  locationId?: string | null,
  merchantId?: string | null,
) {
  return requestData<boolean>(
    "admin/clinic-setup/merchant-onboarding/status",
    "put",
    {
      json: {
        status,
        location_id: locationId,
        ...(merchantId ? { merchant_id: merchantId } : {}),
      },
    },
  );
}

export function linkMerchantToLocation(locationId: string, merchantId: string) {
  return requestData<boolean>(
    "admin/clinic-setup/merchant-onboarding/link",
    "post",
    { json: { location_id: locationId, merchant_id: merchantId } },
  );
}

export function simulateMerchantOnboardingStatus(
  status: string,
  locationId?: string | null,
) {
  return requestData<boolean>(
    "admin/clinic-setup/merchant-onboarding/simulate-status",
    "put",
    { json: { status, location_id: locationId } },
  );
}

export function simulateRegistrationStatus(
  brandState?: string,
  campaignState?: string,
) {
  return requestData<RegistrationStatus>(
    "admin/clinic-setup/phone-setup/simulate-registration-status",
    "put",
    { json: { brand_state: brandState, campaign_state: campaignState } },
  );
}

export function fetchImportReview() {
  return requestData<ImportReviewData>("admin/clinic-setup/import-review");
}

export function reportDataIssue(payload: {
  category: string;
  description: string;
  pageUrl: string;
}) {
  return requestData<{ success: boolean }>(
    "admin/clinic-setup/report-data-issue",
    "post",
    {
      json: {
        category: payload.category,
        description: payload.description,
        page_url: payload.pageUrl,
      },
    },
  );
}

export function submitImportRequest(formData: FormData) {
  return requestData<boolean>("admin/clinic-setup/import-request", "post", {
    body: formData,
  });
}

// ─── SignalWire Brand/Campaign Registration ─────────────────────────────────

export function getBusinessInfoPrefill() {
  return requestData<BusinessInfoPrefill>(
    "admin/clinic-setup/phone-setup/business-info/prefill",
  );
}

export function saveBrandBusinessInfo(payload: BusinessInfoPayload) {
  return requestData<boolean>(
    "admin/clinic-setup/phone-setup/business-info",
    "put",
    {
      json: {
        company_name: payload.companyName,
        dba: payload.dba || null,
        contact_email: payload.contactEmail,
        contact_phone: payload.contactPhone,
        ein: payload.ein,
        legal_entity_type: payload.legalEntityType,
        company_address: payload.companyAddress,
        company_website: payload.companyWebsite,
        privacy_policy_url: payload.privacyPolicyUrl,
      },
    },
  );
}

export function saveCampaignInfo(payload: CampaignInfoPayload) {
  return requestData<boolean>(
    "admin/clinic-setup/phone-setup/campaign-info",
    "put",
    {
      json: {
        description: payload.description,
        sample1: payload.sample1,
        sample2: payload.sample2,
        message_flow: payload.messageFlow,
        opt_in_message: payload.optInMessage,
        opt_out_message: payload.optOutMessage,
        help_message: payload.helpMessage,
      },
    },
  );
}

export function submitSignalWireRegistration() {
  // catchError: false so the caller can read the backend's structured
  // 400 detail (TCR rejection reason). Otherwise the error is swallowed
  // and the wizard just flickers without telling the user why.
  return requestData<RegistrationStatus>(
    "admin/clinic-setup/phone-setup/submit-registration",
    "post",
    undefined,
    false,
  );
}

export function requestPhonePort() {
  // catchError: false so the caller can detect HTTP failures and bail
  // before submitting registration. Otherwise a failed port request
  // would silently log + return null, the SMS registration would still
  // fire, and ops would never be notified that the clinic asked to port.
  return requestData<RegistrationStatus>(
    "admin/clinic-setup/phone-setup/request-port",
    "post",
    undefined,
    false,
  );
}

export function getRegistrationStatus() {
  return requestData<RegistrationStatus>(
    "admin/clinic-setup/phone-setup/registration-status",
  );
}

// ─── Billing Step ────────────────────────────────────────────────────────────

export function getBillingPaymentMethodConfig() {
  return requestData<BillingPaymentMethodConfigResponse>(
    "admin/clinic-setup/billing/payment-method-config",
    "post",
  );
}

export function submitBillingCard(
  rainforestPaymentMethod: Record<string, unknown>,
) {
  return requestData<SubmitBillingCardResponse>(
    "admin/clinic-setup/billing/submit-card",
    "post",
    { json: { rainforest_payment_method: rainforestPaymentMethod } },
  );
}

/**
 * Record that the clinic doesn't need a terminal from us — they already have
 * hardware, or they never take payment in person. Nothing in our data can tell
 * us either, so they have to be able to say so.
 *
 * catchError: false so a 403 (admin-only) reaches the caller instead of
 * resolving to null and looking like it worked.
 */
export function skipPosTerminalSetup(reason?: string) {
  return requestData<boolean>(
    "admin/clinic-setup/pos-terminal/skip",
    "put",
    { json: { reason: reason ?? null } },
    false,
  );
}
